import { Skeleton } from "@/components/Skeleton";
import { useListMCPIntegrations, type MCPIntegration } from "@/queries/integration-queries";
import { useState, useMemo, useCallback, type FC } from "react";
import { AlertTriangle, XCircle, RefreshCw, Settings } from "lucide-react";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import {
    type AgentExecution_Status_MCPIntegrationStatus,
    MCPIntegrationPhase,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import {
    useListHostAuthenticationTokens,
    useDeleteHostAuthenticationToken,
} from "@/queries/runner-configuration-queries";
import type { HostAuthenticationToken } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useDesktop } from "@/hooks/use-desktop";
import { appMainService } from "@/ipc";
import type { PlainMessage } from "@bufbuild/protobuf";
import { Tooltip } from "@/components/Tooltip";
import { Button } from "@/components/flexkit/Button";
import { IconMCP } from "@/assets/icons/geist/IconMCP";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { USER_SETTINGS_MODAL_QUERY_PARAM, parseAsUserSettingsTab } from "@/components/settings/user-settings-constants";
import { useQueryState } from "nuqs";
import { useMembership } from "@/hooks/use-membership";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { Link } from "react-router-dom";

type MCPIntegrationsListProps = {
    organizationId?: string;
    agentExecution: PlainAgentExecution;
};

type MCPServerStatus = PlainMessage<AgentExecution_Status_MCPIntegrationStatus> & {
    integration?: MCPIntegration;
    authToken?: PlainMessage<HostAuthenticationToken>;
};

type MCPItemProps = {
    mcpServer: MCPServerStatus;
};

const MCPItem: FC<MCPItemProps> = ({ mcpServer }) => {
    const { data: user } = useAuthenticatedUser();
    const { isDesktop } = useDesktop();
    const { isMobileViewport } = useIsMobileViewport();
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const deleteTokenMutation = useDeleteHostAuthenticationToken();
    const tooltipMessage = mcpServer.failureMessage || mcpServer.warningMessage;

    const hasAuthToken = !!mcpServer.authToken;
    const isIntegration = !!mcpServer.integration;

    const handleAuthenticate = useCallback(() => {
        if (!user?.id || !mcpServer.integration) return;

        setIsAuthenticating(true);

        let authUrl = `/integrations/oauth/${mcpServer.id}/authorize?principal=${encodeURIComponent(`user/${user.id}`)}`;

        if (authUrl.startsWith("/")) {
            authUrl = `${window.location.origin}${authUrl}`;
        }

        if (isDesktop && appMainService) {
            void appMainService.openExternal({ url: authUrl });
        } else {
            window.open(authUrl, "_blank", "noopener");
        }

        setTimeout(() => {
            setIsAuthenticating(false);
        }, 1000);
    }, [user?.id, isDesktop, mcpServer]);

    const handleRetry = useCallback(async () => {
        if (!mcpServer.authToken) return;
        await deleteTokenMutation.mutateAsync({ tokenId: mcpServer.authToken.id });
    }, [mcpServer.authToken, deleteTokenMutation]);

    const statusIcon =
        mcpServer.phase === MCPIntegrationPhase.MCP_INTEGRATION_PHASE_INITIALIZING ? (
            <IconSpinner size="sm" className="shrink-0 animate-spin" />
        ) : !isIntegration || hasAuthToken ? (
            <>
                {mcpServer.failureMessage ||
                mcpServer.warningMessage ||
                mcpServer.phase !== MCPIntegrationPhase.MCP_INTEGRATION_PHASE_READY ? (
                    <Tooltip content={tooltipMessage}>
                        <span className="inline-flex items-center">
                            {mcpServer.failureMessage && (
                                <XCircle size={16} className="text-red-600 dark:text-red-400" />
                            )}
                            {mcpServer.warningMessage && (
                                <AlertTriangle size={16} className="text-yellow-600 dark:text-yellow-400" />
                            )}
                        </span>
                    </Tooltip>
                ) : (
                    <IconCircleCheck className="size-4 text-content-success" />
                )}
            </>
        ) : null;

    const needsAuth = isIntegration && !hasAuthToken;
    const hasIssue = isIntegration && hasAuthToken && (mcpServer.failureMessage || mcpServer.warningMessage);

    const itemContent = (
        <div className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-2.5 transition-colors hover:bg-surface-secondary">
            <div className="flex items-center gap-2.5">
                {mcpServer.integration?.iconUrl ? (
                    <img src={mcpServer.integration.iconUrl} alt={mcpServer.name} className="size-5 rounded" />
                ) : (
                    <IconMCP size="sm" className="size-5 text-content-secondary" />
                )}
                <span className="text-sm font-medium text-content-primary">{mcpServer.name}</span>
            </div>
            <div className="flex items-center gap-2">
                {needsAuth && (
                    <Button
                        variant="text"
                        size="xs"
                        className="p-0 text-sm font-medium text-content-link hover:no-underline"
                        onClick={handleAuthenticate}
                        disabled={isAuthenticating}
                        data-tracking-id={`mcp-integration-connect-${mcpServer.name}`}
                    >
                        Connect
                    </Button>
                )}
                {hasIssue && (
                    <Button
                        variant="text"
                        size="xs"
                        className="gap-1 p-0 text-sm text-content-primary hover:no-underline"
                        onClick={handleRetry}
                        disabled={deleteTokenMutation.isPending}
                        data-tracking-id={`mcp-integration-retry-${mcpServer.name}`}
                    >
                        <RefreshCw size={14} />
                        Retry
                    </Button>
                )}
                {statusIcon}
            </div>
        </div>
    );

    if (isMobileViewport) {
        return (
            <div>
                {itemContent}
                {tooltipMessage && (!isIntegration || hasAuthToken) && (
                    <div className="px-2 pb-2 pt-1">
                        <div className="rounded-md bg-surface-banner-warning-subtle p-2 text-xs text-content-secondary">
                            {tooltipMessage}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return itemContent;
};

const LoadingState: FC = () => {
    return (
        <div className="flex flex-col gap-2">
            <Skeleton className="h-5 w-32" />
            <div className="flex flex-col gap-1">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
            </div>
        </div>
    );
};

const EmptyState: FC<{ isOrgAdmin: boolean }> = ({ isOrgAdmin }) => {
    return (
        <div className="flex flex-col items-center gap-3 px-4 py-6 text-center">
            <IconMCP size="lg" className="text-content-tertiary" />
            <div className="flex flex-col gap-1">
                <p className="text-normal font-medium text-content-primary">No integrations configured</p>
                <p className="text-sm text-content-secondary">
                    {isOrgAdmin
                        ? "Enable integrations for your organization to connect external tools."
                        : "Ask your organization admin to enable integrations, or configure MCP servers."}
                </p>
            </div>
            {isOrgAdmin ? (
                <Button variant="secondary" size="sm" asChild data-tracking-id="mcp-empty-state-org-settings">
                    <Link to="/settings/org-integrations">Go to Organization settings</Link>
                </Button>
            ) : (
                <div className="flex items-center gap-3 text-sm">
                    <ExternalLink href="https://ona.com/docs/ona/mcp" iconSize="sm">
                        Learn more about MCP servers
                    </ExternalLink>
                </div>
            )}
        </div>
    );
};

export const MCPIntegrationsList: FC<MCPIntegrationsListProps> = ({ organizationId, agentExecution }) => {
    const { data: integrations, isLoading: integrationsLoading } = useListMCPIntegrations(organizationId);
    const { data: authTokensData, isLoading: tokensLoading } = useListHostAuthenticationTokens({ enabled: true });
    const [, setUserSettingsTab] = useQueryState(USER_SETTINGS_MODAL_QUERY_PARAM, parseAsUserSettingsTab);
    const { isMobileViewport } = useIsMobileViewport();
    const { membership } = useMembership();
    const isOrgAdmin = membership?.userRole === OrganizationRole.ADMIN;

    const isLoading = integrationsLoading || tokensLoading;

    // Merge integration data with status from agent execution
    const { mcpServersWithStatus } = useMemo(() => {
        if (!integrations || !authTokensData) {
            return {
                mcpServersWithStatus: [],
            };
        }

        const mcpStatuses = agentExecution.status?.mcpIntegrationStatuses || [];
        const tokensMap = new Map<string, PlainMessage<HostAuthenticationToken>>();
        for (const token of authTokensData.tokens) {
            if (token.integrationId) {
                tokensMap.set(token.integrationId, token);
            }
        }
        const serversWithStatus: MCPServerStatus[] = [];

        // Add all MCP integrations
        for (const integration of integrations) {
            const status = mcpStatuses.find((s) => s.id === integration.id);
            serversWithStatus.push({
                id: integration.id,
                name: integration.name,
                phase: status?.phase ?? MCPIntegrationPhase.MCP_INTEGRATION_PHASE_UNAVAILABLE,
                failureMessage: status?.failureMessage ?? "",
                warningMessage: status?.warningMessage ?? "",
                integration,
                authToken: tokensMap.get(integration.id),
            });
        }

        // Add standalone MCP servers from .ona folder
        // These have empty id since they're not linked to any integration
        for (const status of mcpStatuses) {
            if (status.id === "") {
                serversWithStatus.push({
                    id: "",
                    name: status.name,
                    phase: status.phase,
                    failureMessage: status.failureMessage,
                    warningMessage: status.warningMessage,
                    // No integration property - this is a standalone MCP server
                });
            }
        }

        return { mcpServersWithStatus: serversWithStatus };
    }, [integrations, authTokensData, agentExecution.status?.mcpIntegrationStatuses]);

    const handleManageClick = useCallback(() => {
        void setUserSettingsTab("integrations");
    }, [setUserSettingsTab]);

    if (isLoading) {
        return <LoadingState />;
    }

    if (!mcpServersWithStatus || mcpServersWithStatus.length === 0) {
        return <EmptyState isOrgAdmin={isOrgAdmin} />;
    }

    const integrationItems = mcpServersWithStatus.filter((s) => !!s.integration);
    const mcpServerItems = mcpServersWithStatus.filter((s) => !s.integration);

    return (
        <div
            className="flex flex-col rounded-xl focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            tabIndex={0}
        >
            <div className="flex items-center justify-between px-2 py-2">
                {!isMobileViewport && <h3 className="text-sm font-semibold text-content-primary">MCP Integrations</h3>}
                <Button
                    variant="ghost"
                    size="xs"
                    className="ml-auto gap-1.5 text-sm text-content-secondary hover:text-content-primary"
                    onClick={handleManageClick}
                    data-tracking-id="mcp-integrations-manage"
                >
                    <Settings size={14} />
                    Manage
                </Button>
            </div>

            {integrationItems.length > 0 && (
                <div className="flex flex-col">
                    {integrationItems.map((mcpServer) => (
                        <MCPItem
                            key={mcpServer.id}
                            mcpServer={mcpServer}
                            data-tracking-id={`integration-status-${mcpServer.id}`}
                        />
                    ))}
                </div>
            )}
            {mcpServerItems.length > 0 && (
                <>
                    {integrationItems.length > 0 && <div className="mx-2 border-t border-border-light" />}
                    <div className="flex flex-col">
                        {mcpServerItems.map((mcpServer) => (
                            <MCPItem
                                key={mcpServer.name}
                                mcpServer={mcpServer}
                                data-tracking-id={`integration-status-${mcpServer.name}`}
                            />
                        ))}
                    </div>
                </>
            )}

            <div className="flex items-center justify-center gap-3 px-2 pb-2 pt-1 text-sm">
                <ExternalLink href="https://ona.com/docs/ona/mcp" iconSize="sm">
                    Learn more about MCP servers
                </ExternalLink>
            </div>
        </div>
    );
};
